import { addAdditionalHeadersToPLAPI, makeDownstreamApiCall } from "@swiggy-private/checkout-core";
import { ApiResponse } from "@payment-client/interfaces/Api";
import {
    VerifyVpaParams,
    VerifyVpaResponse,
    GetPaymentFallbackMethodsParams,
    UpdateFrequencyCounterResponse,
    CreateTransactionParams,
} from "@payment-client/interfaces/PaymentPublic";
import { PaymentFallbacksApiResponse } from "@swiggy-private/payment-ui";
import * as PennyTxnApi from "@swiggy-private/payment-types";
import * as PaymentApi from "@payment-client/interfaces/PaymentApi";
import { BannerParams } from "@payment-client/interfaces/Banner";
import {
    createMockPennyTransaction,
    createMockConfirmPennyTransaction,
    shouldUseMockCreateTransaction,
    shouldUseMockConfirmTransaction,
} from "../mocks/publicPaymentServiceMocks";

export const ENDPOINTS = {
    VERIFY_VPA: "/api/v3/vpa-verify",
    GET_BANKS_LIST: "/api/v3/get-banks-list",
    GET_PAYMENT_FALLBACK_METHODS: "/api/v3/get-payment-fallback-methods",
    CHECK_TXN_STATUS: "/api/v3/vpa-txn-status",
    CREATE_PENNY_TRANSACTION: "/api/v3/create-penny-transaction",
    CONFIRM_PENNY_TRANSACTION: "/api/v3/confirm-penny-transaction",
    UPDATE_FREQUENCY_COUNTER: "/api/v1/json/update_frequency_counter",
};

export async function getBanksList(apiHost: string): Promise<ApiResponse<PaymentApi.BankListData>> {
    const response = await makeDownstreamApiCall<PaymentApi.BankListData>({
        apiHost,
        endpoint: ENDPOINTS.GET_BANKS_LIST,
        options: {
            method: "GET",
        },
    });
    return response as ApiResponse<PaymentApi.BankListData>;
}

export async function verifyVpa(
    apiHost: string,
    params: VerifyVpaParams & { [key: string]: unknown },
): Promise<ApiResponse<VerifyVpaResponse>> {
    const response = await makeDownstreamApiCall<VerifyVpaResponse>({
        apiHost,
        endpoint: ENDPOINTS.VERIFY_VPA,
        bodyParams: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payer_vpa: params.payerVpa,
        },
        options: {
            method: "POST",
        },
    });
    return response as ApiResponse<VerifyVpaResponse>;
}

export async function getPaymentFallbackMethods(
    apiHost: string,
    params: GetPaymentFallbackMethodsParams,
): Promise<ApiResponse<PaymentFallbacksApiResponse>> {
    const regularHeaders: Record<string, string> = {};
    if (params.cartKey) {
        regularHeaders.cartkey = params.cartKey;
    }

    const response = await makeDownstreamApiCall<PaymentFallbacksApiResponse>({
        apiHost,
        endpoint: ENDPOINTS.GET_PAYMENT_FALLBACK_METHODS,
        searchParams: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            order_id: params.orderId,
        },
        options: {
            headers: regularHeaders,
            method: "GET",
        },
    });
    return response as ApiResponse<PaymentFallbacksApiResponse>;
}

export async function checkTransactionStatus(
    apiHost: string,
    transactionId: string,
    paymentCode?: string,
): Promise<ApiResponse<string>> {
    const response = await makeDownstreamApiCall<{ [key: string]: unknown }>({
        apiHost,
        endpoint: ENDPOINTS.CHECK_TXN_STATUS,
        searchParams: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            paas_id: transactionId,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payment_code: paymentCode || "",
        },
        options: {
            method: "GET",
        },
    });
    return response as ApiResponse<string>;
}

export async function createPennyTransaction(
    apiHost: string,
    params: CreateTransactionParams & { [key: string]: unknown },
): Promise<PennyTxnApi.CreatePennyTransactionResponse> {
    if (shouldUseMockCreateTransaction()) {
        return createMockPennyTransaction(params);
    }

    const response = await makeDownstreamApiCall<PennyTxnApi.CreatePennyTransactionResponse>({
        apiHost,
        endpoint: ENDPOINTS.CREATE_PENNY_TRANSACTION,
        bodyParams: params,
        options: {
            method: "POST",
        },
    });
    return response.data as PennyTxnApi.CreatePennyTransactionResponse;
}

export async function confirmPennyTransaction(
    apiHost: string,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params: { order_id: string },
): Promise<ApiResponse<PennyTxnApi.ConfirmPennyTransactionResponse>> {
    if (shouldUseMockConfirmTransaction()) {
        return createMockConfirmPennyTransaction();
    }

    const response = await makeDownstreamApiCall<PennyTxnApi.ConfirmPennyTransactionResponse>({
        apiHost,
        endpoint: ENDPOINTS.CONFIRM_PENNY_TRANSACTION,
        bodyParams: params,
        searchParams: params,
        options: {
            method: "POST",
        },
    });
    return response as ApiResponse<PennyTxnApi.ConfirmPennyTransactionResponse>;
}

export async function updateFrequencyCounter(
    apiHost: string,
    params: BannerParams & { [key: string]: unknown },
): Promise<ApiResponse<UpdateFrequencyCounterResponse>> {
    const response = await makeDownstreamApiCall<UpdateFrequencyCounterResponse>({
        apiHost,
        endpoint: ENDPOINTS.UPDATE_FREQUENCY_COUNTER,
        bodyParams: params,
        options: {
            method: "POST",
            headers: addAdditionalHeadersToPLAPI(),
        },
    });
    return response as ApiResponse<UpdateFrequencyCounterResponse>;
}
